import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import clsx from 'clsx';
import { CloseButton } from '../dialog-common/dialog-common.js';
export { ActionStrip, BackButton, Body, Contents, CustomCloseButton, CustomContents, CustomFooter, CustomFormContents, CustomHeader, Footer, FormContents, Header, HiddenTitle, TabStrip, Title } from '../dialog-common/dialog-common.js';
import { DialogCustomCloseProvider, useHasCustomClose } from '../dialog-common/dialog-common.context.js';
export { useClose } from '../dialog-common/dialog-common.context.js';
import * as dialogCommon_pages from '../dialog-common/dialog-common.pages.js';
export { dialogCommon_pages as Pages };
export { ResizableSidebar, Sidebar } from '../dialog-common/dialog-common.sidebar.js';
import { Root as Root$1 } from '../modal-primitive/modal-primitive.js';
export { useModal, useModalManager, useUncontrolledModal } from '../modal-primitive/modal-primitive.js';
import { parseSize, useBreakpoint } from './modal.utils.js';
import { noCloseButton } from '../dialog-common/dialog-common.css.js';
import { backdrop, contents, root } from './modal.css.js';

/**
 * Base component for `Modal`, which is used when there's a "fork in the road" and
 * the user needs to make a choice immediately.
 *
 * @compose
 * ```json
 * {
 *   "root": true,
 *   "descendants": [
 *     "Modal.Contents",
 *     "Modal.FormContents",
 *     "Modal.CustomContents",
 *     "Modal.CustomFormContents",
 *     "Modal.HiddenTitle"
 *   ]
 * }
 * ```
 * @see https://fpl.figma.design/components/modal/
 */
const Root = forwardRef(({ children, width, height = 'fixed', fullscreen, maxWidth, maxHeight, overrideCloseButtonColor, ...props }, ref) => {
    const size = parseSize(width, height, maxWidth, maxHeight, fullscreen);
    const isSmallViewport = useBreakpoint(size.breakpoint);
    // Determine preventUserClose from the right source
    const preventUserClose = props.preventUserClose ??
        (props.manager && 'preventUserClose' in props.manager
            ? props.manager.preventUserClose
            : false);
    return (jsx(Root$1, { "data-fpl-component": "", ...props, ref: ref, preventUserClose: preventUserClose, "data-modal-fullscreen": isSmallViewport || undefined, styles: {
            root: { className: clsx(root, size.className), style: size.style },
            contents: clsx(contents, { [noCloseButton]: preventUserClose }),
            backdrop: backdrop,
        }, children: jsx(DialogCustomCloseProvider, { children: jsx(ModalChildren, { closeButton: !preventUserClose && jsx(CloseButton, { overrideColor: overrideCloseButtonColor }), children: children }) }) }));
});
Root.displayName = 'Modal.Root';
// eslint-disable-next-line react/display-name
function ModalChildren({ children, closeButton, }) {
    const hasCustomClose = useHasCustomClose();
    return (jsxs(Fragment, { children: [children, hasCustomClose ? null : closeButton] }));
}

export { Root };
//# sourceMappingURL=modal.js.map
